export const INBOUND_EMAIL_DOMAIN = "emails.fabera.fr";

export const INBOUND_EMAIL_ENVIRONMENTS = ["development", "staging", "production"] as const;

export type InboundEmailEnvironment = (typeof INBOUND_EMAIL_ENVIRONMENTS)[number];

const SUFFIX_BY_ENVIRONMENT = {
  development: "dev",
  staging: "staging",
  production: null,
} as const satisfies Record<InboundEmailEnvironment, string | null>;

const ENVIRONMENT_BY_SUFFIX = new Map<string, InboundEmailEnvironment>(
  INBOUND_EMAIL_ENVIRONMENTS.flatMap((environment) => {
    const suffix = SUFFIX_BY_ENVIRONMENT[environment];
    return suffix ? [[suffix, environment] as const] : [];
  }),
);

function extractEmailAddress(recipient: string): string {
  const angleBracketMatch = /<([^>]+)>/.exec(recipient);
  return (angleBracketMatch?.[1] ?? recipient).trim().toLowerCase();
}

export function parseInboundEmailAddress(recipient: string): {
  slug: string;
  environment: InboundEmailEnvironment;
} {
  const localPart = extractEmailAddress(recipient).split("@")[0] ?? "";
  const segments = localPart.split(".");
  const lastSegment = segments.length > 1 ? segments[segments.length - 1]! : null;
  const environment = lastSegment ? ENVIRONMENT_BY_SUFFIX.get(lastSegment) : undefined;

  if (!environment) {
    return { slug: localPart, environment: "production" };
  }

  return { slug: segments.slice(0, -1).join("."), environment };
}

export function buildInboundEmailAddress(
  slug: string,
  environment: InboundEmailEnvironment,
): string {
  const suffix = SUFFIX_BY_ENVIRONMENT[environment];
  const localPart = suffix ? `${slug}.${suffix}` : slug;
  return `${localPart}@${INBOUND_EMAIL_DOMAIN}`;
}
